/*
 * Copyright (C) 2024 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Component, Input, OnInit } from '@angular/core';
import { SimpleChartData } from '@app/app/shared/components/carts/SimpleChartData';
import { ChartConfiguration, ChartDataset, ChartType } from 'chart.js';

const V3_COLORS = ['#4338ca', '#0baf96', '#e8634a', '#d97706'];

@Component({
  selector: 'ptl-simple-pie-chart',
  templateUrl: './simple-pie-chart.component.html',
  styleUrls: ['./simple-pie-chart.component.scss'],
})
export class SimplePieChartComponent implements OnInit {
  @Input() chartData: SimpleChartData[];

  @Input() variant: 'legacy' | 'v3' = 'legacy';

  chartDataset: ChartDataset[] = [];
  chartLabels: string[];
  chartOptions: ChartConfiguration<'pie' | 'doughnut'>['options'];

  pieSegments: { label: string; value: string; color: string }[] = [];
  baseColors = ['#4c4cff', '#00bfa5', '#ff4081', '#ff9100'];

  get chartType(): ChartType {
    return this.variant === 'v3' ? 'doughnut' : 'pie';
  }

  private get colors(): string[] {
    return this.variant === 'v3' ? V3_COLORS : this.baseColors;
  }

  ngOnInit(): void {
    if (this.chartData) {
      this.setChartOptions();
      this.showPieChart();
    }
  }

  private showPieChart(): void {
    const labels: string[] = [];
    for (const item of this.chartData) {
      labels.push(item.label);
    }
    this.chartDataset = this.getChartData();
    this.chartLabels = labels;
  }

  private getChartData(): ChartDataset[] {
    const dataset = {
      data: [],
      backgroundColor: [],
      hoverBackgroundColor: [],
      borderColor: [],
      borderWidth: this.variant === 'v3' ? 2 : 1,
      pointRadius: 0,
      pointHoverRadius: 0,
    };

    this.chartData.forEach((item, index) => {
      const color = this.getColor(index);
      dataset.data.push(item.score);
      dataset.borderColor.push('#ffffff');
      dataset.backgroundColor.push(color);
      dataset.hoverBackgroundColor.push(color);
      this.pieSegments.push({
        label: item.label,
        value: this.toSelectedValueLabel(item.selectedCount, item.allResponsesCount),
        color: color,
      });
    });

    return [dataset];
  }

  private toSelectedValueLabel(selectedCount: number, totalCount: number): string {
    if (!selectedCount || !totalCount) {
      return `${selectedCount}`;
    }
    const percentage = ((selectedCount / totalCount) * 100).toFixed(1);
    return `${selectedCount} (${percentage}%)`;
  }

  private getColor(index: number): string {
    if (index < this.colors.length) {
      return this.colors[index];
    }
    const hue = (index * 137.5) % 360;
    return `hsl(${hue}, 70%, 60%)`;
  }

  private setChartOptions(): void {
    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      cutout: this.variant === 'v3' ? '58%' : 0,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
        },
      },
    };
  }
}
